import { Controller } from "@hotwired/stimulus";

/**
 * Post Lock Polling Controller
 * 
 * Polls the lock_status endpoint every 5 seconds to get post lock state
 * and updates the UI dynamically, reusing the same logic as ActionCable.
 * 
 * Compares previous/current state and only updates if changed.
 * Works on both edit page and lock_warning page.
 */
export default class extends Controller {
  static values = {
    postId: String,
    currentUserId: String,
    pollInterval: { type: Number, default: 5000 } // 5 seconds default
  }

  connect() {
    console.log("🔄🔄🔄 POST LOCK POLLING CONTROLLER CONNECTING 🔄🔄🔄");
    console.log("  - Timestamp:", new Date().toISOString());
    console.log("  - Post ID:", this.postIdValue);
    console.log("  - Current User ID:", this.currentUserIdValue);
    console.log("  - Poll Interval:", this.pollIntervalValue, "ms");
    console.log("  - Element:", this.element);
    
    if (!this.postIdValue) {
      console.error("❌ Post Lock Polling: Post ID not found");
      return;
    }

    if (!this.currentUserIdValue) {
      console.error("❌ Post Lock Polling: Current User ID not found");
      return;
    }

    // Store previous state for comparison
    this.previousState = null;
    this.isPolling = false;
    this.pollTimer = null;

    // Listen for custom events to trigger immediate polls
    this.element.addEventListener('trigger-poll', () => {
      console.log('🔄 Received trigger-poll event, performing immediate poll');
      this.previousState = null; // Reset to force update detection
      this.performPoll();
    });

    // On tab visible again: check lock; if user no longer has lock, reload so UI is correct
    this.boundOnVisibilityChange = () => this.onVisibilityChange();
    document.addEventListener('visibilitychange', this.boundOnVisibilityChange);

    // When switching back from another app (e.g. Cursor), visibilitychange/blur/focus often don't fire.
    // So we also poll document.hasFocus() every 1s; when we see false then true, we "returned".
    this.boundOnWindowBlur = () => this.onWindowBlur();
    this.boundOnWindowFocus = () => this.onWindowFocus();
    window.addEventListener('blur', this.boundOnWindowBlur);
    window.addEventListener('focus', this.boundOnWindowFocus);
    document.addEventListener('focus', this.boundOnWindowFocus);

    this._windowHadBlur = false;
    this._hasFocusCheckInterval = setInterval(() => {
      const focused = document.hasFocus();
      if (!focused) {
        this._windowHadBlur = true;
      } else if (this._windowHadBlur) {
        console.log('[hasFocus poll] document.hasFocus() true again — running lock check (returned to browser)');
        this._windowHadBlur = false;
        this.runLockCheckWhenReturning();
      }
    }, 1000);

    // Start polling
    this.startPolling();
    
    console.log("✅ Post Lock Polling Controller connected and started");
  }

  disconnect() {
    this.stopPolling();
    if (this.boundOnVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundOnVisibilityChange);
    }
    if (this.boundOnWindowBlur) {
      window.removeEventListener('blur', this.boundOnWindowBlur);
    }
    if (this.boundOnWindowFocus) {
      window.removeEventListener('focus', this.boundOnWindowFocus);
      document.removeEventListener('focus', this.boundOnWindowFocus);
    }
    if (this._hasFocusCheckInterval) {
      clearInterval(this._hasFocusCheckInterval);
      this._hasFocusCheckInterval = null;
    }
    if (this._focusCheckInterval) {
      clearInterval(this._focusCheckInterval);
      this._focusCheckInterval = null;
    }
    console.log("🛑 Post Lock Polling Controller disconnected");
  }

  onWindowBlur() {
    console.log('[window blur] fired — left browser window');
    this._windowHadBlur = true;
    if (this._focusCheckInterval) clearInterval(this._focusCheckInterval);
    this._focusCheckInterval = setInterval(() => {
      if (document.hasFocus() && this._windowHadBlur) {
        console.log('[focus fallback] document.hasFocus() true after blur — running lock check');
        clearInterval(this._focusCheckInterval);
        this._focusCheckInterval = null;
        this._windowHadBlur = false;
        this.runLockCheckWhenReturning();
      }
    }, 500);
    setTimeout(() => {
      if (this._focusCheckInterval) {
        clearInterval(this._focusCheckInterval);
        this._focusCheckInterval = null;
      }
    }, 30000);
  }

  onWindowFocus() {
    if (this._focusCheckInterval) {
      clearInterval(this._focusCheckInterval);
      this._focusCheckInterval = null;
    }
    if (!this._windowHadBlur) return;
    this._windowHadBlur = false;
    console.log('[window/document focus] fired — running lock check (returned to browser)');
    this.runLockCheckWhenReturning();
  }

  onVisibilityChange() {
    console.log('[visibilitychange] fired', {
      hidden: document.hidden,
      visibilityState: document.visibilityState
    });
    if (document.visibilityState !== 'visible') {
      console.log('[visibilitychange] not visible, skipping lock check');
      return;
    }
    console.log('[visibilitychange] tab visible — running lock check');
    this.runLockCheckWhenReturning();
  }

  /** Debounced so visibilitychange + focus don't both trigger a check in quick succession. */
  runLockCheckWhenReturning() {
    const now = Date.now();
    if (this._lastLockCheckOnReturn != null && now - this._lastLockCheckOnReturn < 1500) {
      console.log('[runLockCheckWhenReturning] skipped (called recently)');
      return;
    }
    this._lastLockCheckOnReturn = now;
    this.checkLockAndReloadIfNeeded();
  }

  /**
   * Fetch lock status; if we're on the edit form and current user doesn't have the lock, reload.
   * Used when tab becomes visible so we show the correct UI (e.g. lock warning) without relying on polling.
   */
  checkLockAndReloadIfNeeded() {
    const url = `/admin/posts/${this.postIdValue}/lock_status`;
    console.log('[checkLockAndReloadIfNeeded] fetching', url);
    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })
      .then(res => res.ok ? res.json() : Promise.reject(new Error(res.status)))
      .then(data => {
        const isOwner = data.locked_by_id != null &&
          data.locked_by_id.toString() === this.currentUserIdValue;
        const editFormPresent = document.querySelector('form[action*="/admin/posts"]') != null;
        const someoneElseHasLock = data.locked && data.locked_by_id != null &&
          data.locked_by_id.toString() !== this.currentUserIdValue;
        const noOneHasLock = !data.locked || data.locked_by_id == null;
        console.log('[checkLockAndReloadIfNeeded] result', {
          locked: data.locked,
          locked_by_id: data.locked_by_id,
          currentUserId: this.currentUserIdValue,
          isOwner,
          editFormPresent,
          someoneElseHasLock,
          noOneHasLock
        });
        if (!editFormPresent || isOwner) {
          console.log('[checkLockAndReloadIfNeeded] keeping page (have lock or not on edit form)');
          return;
        }
        if (someoneElseHasLock) {
          console.log('[checkLockAndReloadIfNeeded] someone else has lock → reloading');
          window.location.reload();
          return;
        }
        if (noOneHasLock) {
          console.log('[checkLockAndReloadIfNeeded] no one has lock → re-acquiring lock');
          this.reacquireLockAndContinue();
          return;
        }
      })
      .catch(err => {
        console.warn('[checkLockAndReloadIfNeeded] lock check failed:', err);
      });
  }

  /**
   * POST to acquire_lock; on success continue editing (no reload), on failure reload.
   */
  reacquireLockAndContinue() {
    const url = `/admin/posts/${this.postIdValue}/acquire_lock`;
    const token = this.getCSRFToken();
    const body = new URLSearchParams({ authenticity_token: token }).toString();
    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': token,
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.locked) {
          console.log('[reacquireLockAndContinue] lock re-acquired, continuing without reload');
        } else {
          console.warn('[reacquireLockAndContinue] failed to re-acquire:', data.message, '→ reloading');
          window.location.reload();
        }
      })
      .catch(err => {
        console.warn('[reacquireLockAndContinue] request failed:', err, '→ reloading');
        window.location.reload();
      });
  }

  startPolling() {
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;
    console.log("🔄 Starting polling with interval:", this.pollIntervalValue, "ms");
    
    // Poll immediately, then set interval
    this.performPoll();
    this.pollTimer = setInterval(() => {
      this.performPoll();
    }, this.pollIntervalValue);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.isPolling = false;
    console.log("🛑 Polling stopped");
  }

  performPoll() {
    if (!this.isPolling) {
      return;
    }

    const url = `/admin/posts/${this.postIdValue}/lock_status`;
    console.log("📡 Polling lock status:", url);

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (!response.ok) {
        console.error(`❌ HTTP error! status: ${response.status}`);
        if (response.status === 403) {
          console.error("❌ Authorization error - user may not have permission to view lock status");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      console.log("📨📨📨📨📨 POLLING RESPONSE RECEIVED 📨📨📨📨📨");
      console.log("  - Timestamp:", new Date().toISOString());
      console.log("  - Post ID:", data.post_id);
      console.log("  - Locked:", data.locked);
      console.log("  - Locked by ID:", data.locked_by_id);
      console.log("  - Current User ID:", this.currentUserIdValue);
      console.log("  - Pending requests count:", (data.pending_requests || []).length);
      console.log("  - All requests count:", (data.all_requests || []).length);
      
      // Compare with previous state
      // Check if we're on the lock warning page - if so, always update UI
      // This ensures requesters see status changes even if state comparison doesn't detect them
      const isOnLockWarningPage = document.getElementById('lock-request-status') !== null;
      const shouldForceUpdate = isOnLockWarningPage;
      
      if (this.hasStateChanged(data) || shouldForceUpdate) {
        if (shouldForceUpdate && !this.hasStateChanged(data)) {
          console.log("🔄 On lock warning page, forcing UI update even if state appears unchanged");
        } else {
          console.log("✅ State changed, updating UI");
        }
        this.previousState = this.deepClone(data);
        this.handleStateUpdate(data);
      } else {
        console.log("⏭️ State unchanged, skipping UI update");
      }
    })
    .catch(error => {
      console.error("❌ Error polling lock status:", error);
      console.error("  - Error message:", error.message);
      // Don't stop polling on error, just log it
      // Continue polling even if one request fails
    });
  }

  hasStateChanged(newState) {
    if (!this.previousState) {
      return true; // First poll, always update
    }

    // Compare key fields
    const prev = this.previousState;
    const curr = newState;

    // Compare locked status
    if (prev.locked !== curr.locked) {
      console.log("  - Locked status changed:", prev.locked, "->", curr.locked);
      return true;
    }

    // Compare locked_by_id
    if (prev.locked_by_id !== curr.locked_by_id) {
      console.log("  - Locked by ID changed:", prev.locked_by_id, "->", curr.locked_by_id);
      return true;
    }

    // Compare pending requests count
    const prevPendingCount = (prev.pending_requests || []).length;
    const currPendingCount = (curr.pending_requests || []).length;
    if (prevPendingCount !== currPendingCount) {
      console.log("  - Pending requests count changed:", prevPendingCount, "->", currPendingCount);
      return true;
    }

    // Compare pending requests IDs (in case requests were added/removed)
    const prevRequestIds = (prev.pending_requests || []).map(r => r.id).sort().join(',');
    const currRequestIds = (curr.pending_requests || []).map(r => r.id).sort().join(',');
    if (prevRequestIds !== currRequestIds) {
      console.log("  - Pending request IDs changed:", prevRequestIds, "->", currRequestIds);
      return true;
    }

    // Compare request statuses (in case a request status changed)
    // Use all_requests if available for more comprehensive comparison
    const prevAllRequests = prev.all_requests || prev.pending_requests || [];
    const currAllRequests = curr.all_requests || curr.pending_requests || [];
    
    // Compare all_requests count (in case new requests were added)
    if (prevAllRequests.length !== currAllRequests.length) {
      console.log("  - All requests count changed:", prevAllRequests.length, "->", currAllRequests.length);
      return true;
    }
    
    // Compare request statuses (more detailed comparison)
    const prevRequestStatuses = prevAllRequests.map(r => `${r.id}:${r.status}`).sort().join(',');
    const currRequestStatuses = currAllRequests.map(r => `${r.id}:${r.status}`).sort().join(',');
    if (prevRequestStatuses !== currRequestStatuses) {
      console.log("  - Request statuses changed");
      console.log("    - Previous:", prevRequestStatuses);
      console.log("    - Current:", currRequestStatuses);
      return true;
    }
    
    // Compare individual request fields that might change (like updated_at timestamps)
    // This helps detect when a request status changes but the comparison above misses it
    for (let i = 0; i < currAllRequests.length; i++) {
      const currReq = currAllRequests[i];
      const prevReq = prevAllRequests.find(r => r.id === currReq.id);
      if (prevReq) {
        // Check if status changed
        if (prevReq.status !== currReq.status) {
          console.log("  - Request status changed for ID", currReq.id, ":", prevReq.status, "->", currReq.status);
          return true;
        }
      }
    }

    // No changes detected
    return false;
  }

  deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  handleStateUpdate(data) {
    console.log("🔄 handleStateUpdate called (polling)");
    console.log("  - Data received:", data);
    
    if (!data) {
      console.error("❌ No data received in handleStateUpdate");
      return;
    }
    
    if (data.type !== "post_lock_state") {
      console.warn("⚠️ Unknown message type:", data.type);
      return;
    }

    console.log("✅ Valid post_lock_state message received");
    
    const isOwner = data.locked_by_id && data.locked_by_id.toString() === this.currentUserIdValue;
    // Use all_requests if available to check for any request by this user
    const allRequests = data.all_requests || data.pending_requests || [];
    const isRequester = allRequests.some(req => 
      req.requester_id.toString() === this.currentUserIdValue
    );
    const hasPendingRequest = (data.pending_requests || []).some(req => 
      req.requester_id.toString() === this.currentUserIdValue && req.status === "pending"
    );

    console.log("📊 State analysis:", {
      locked: data.locked,
      locked_by_id: data.locked_by_id,
      current_user_id: this.currentUserIdValue,
      isOwner,
      isRequester,
      hasPendingRequest,
      pendingRequestsCount: (data.pending_requests || []).length,
      allRequestsCount: allRequests.length
    });

    // Reuse the same rendering logic from ActionCable controller
    this.updateUI(data, isOwner, isRequester, hasPendingRequest);
  }

  updateUI(data, isOwner, isRequester, hasPendingRequest) {
    // Reuse the same logic from post_lock_channel_controller.js
    // Update UI based on user role
    
    // Check if we're on the lock_warning page (has lock-request-status div)
    const isOnLockWarningPage = document.getElementById('lock-request-status') !== null;
    
    // IMPORTANT: If user just became the owner, handle redirect first
    // This happens when a request is approved and lock is transferred
    // Check if we're on the lock warning page OR if previous state showed user was NOT the owner
    if (isOwner) {
      const wasNotOwner = this.previousState &&
                         this.previousState.locked_by_id &&
                         this.previousState.locked_by_id.toString() !== this.currentUserIdValue;

      if (isOnLockWarningPage || wasNotOwner) {
        // User just became the owner (request was approved) - handle redirect
        console.log("🎉 User just became the lock owner (request approved) - handling redirect");
        console.log("  - isOnLockWarningPage:", isOnLockWarningPage);
        console.log("  - wasNotOwner:", wasNotOwner);
        console.log("  - Previous locked_by_id:", this.previousState?.locked_by_id);
        console.log("  - Current locked_by_id:", data.locked_by_id);
        this.handleRequestApproved(data);
        return; // Don't continue with normal UI updates, redirect is happening
      }
    }

    if (isOwner) {
      console.log("👑 User is owner, updating owner UI");
      this.updateOwnerUI(data);
    } else if (isOnLockWarningPage || isRequester || hasPendingRequest) {
      // If user is on lock_warning page OR is a requester OR has pending request, update requester UI
      // This ensures requesters get updates even before they create a request
      console.log("🙋 User is requester or on lock warning page, updating requester UI");
      console.log("  - isOnLockWarningPage:", isOnLockWarningPage);
      console.log("  - isRequester:", isRequester);
      console.log("  - hasPendingRequest:", hasPendingRequest);
      this.updateRequesterUI(data);
    } else if (data.locked && !isOwner) {
      console.log("🔒 Post locked by another user");
      this.handleLockStateForNonOwner(data);
    } else {
      console.log("🔓 Post unlocked or user can edit");
      this.updateUnlockedUI(data);
    }
  }

  handleLockStateForNonOwner(data) {
    // If the current user is now the lock owner, they got approved
    if (data.locked_by_id && data.locked_by_id.toString() === this.currentUserIdValue) {
      console.log("✅ User is now the lock owner - request was approved!");
      this.handleRequestApproved(data);
    } else {
      // Otherwise, just show the locked state
      this.updateLockedByOtherUI(data);
    }
  }

  updateOwnerUI(data) {
    console.log("👑👑👑 Updating Owner UI (polling)");
    console.log("  - Pending requests count:", data.pending_requests?.length || 0);
    
    // Update pending requests list
    const requestsContainer = document.getElementById("lock-takeover-requests");
    const modalsContainer = document.getElementById("lock-takeover-modals");
    
    if (requestsContainer) {
      // Clear existing requests
      requestsContainer.innerHTML = "";
      
      // Add each pending request (only those the current user can act on as lock owner)
      const currentUserId = this.currentUserIdValue.toString();
      const lockedById = data.locked_by_id?.toString();
      const pendingForOwner = (data.pending_requests || []).filter(
        r => r.status === "pending" && r.owner_id?.toString() === lockedById && lockedById === currentUserId
      );
      const pendingCount = pendingForOwner.length;

      pendingForOwner.forEach(request => {
        this.appendModalForRequest(request);
      });
      
      // Update count
      const heading = document.getElementById("pending-requests-heading");
      const parentContainer = document.getElementById("pending-takeover-requests-container");
      
      if (pendingCount > 0) {
        if (heading) {
          heading.textContent = `Pending Takeover Requests (${pendingCount})`;
        }
        if (parentContainer) {
          parentContainer.style.display = "none";
        }
      } else {
        if (parentContainer) {
          parentContainer.style.display = "none";
        }
      }
    }
    
    // Remove modals for non-pending requests
    if (modalsContainer) {
      const allModals = modalsContainer.querySelectorAll('[id^="lock-takeover-modal-"]');
      
      allModals.forEach(modal => {
        const requestId = modal.id.replace("lock-takeover-modal-", "");
        const request = (data.pending_requests || []).find(r => r.id.toString() === requestId);
        if (!request || request.status !== "pending") {
          const backdrop = document.getElementById(`lock-takeover-modal-backdrop-${requestId}`);
          modal.remove();
          if (backdrop) backdrop.remove();
        }
      });
      
      // Remove body class if no modals
      const remainingModals = modalsContainer.querySelectorAll('.lock-takeover-modal-content');
      if (remainingModals.length === 0) {
        document.body.classList.remove('modal-open');
      }
    }
    
    console.log("✅ Owner UI update completed");
  }

  updateRequesterUI(data) {
    console.log("🙋🙋🙋 Updating Requester UI (polling)");
    console.log("  - Locked:", data.locked);
    console.log("  - Locked by ID:", data.locked_by_id);
    console.log("  - Current User ID:", this.currentUserIdValue);
    
    // Check if user is now the lock owner (request was approved)
    const isNowOwner = data.locked_by_id && data.locked_by_id.toString() === this.currentUserIdValue;
    
    if (isNowOwner) {
      console.log("✅✅✅ User is now the lock owner - request was approved!");
      console.log("  - Calling handleRequestApproved immediately");
      this.handleRequestApproved(data);
      return;
    }

    // Always update "edited by" name when we have lock state (e.g. after lock transferred to someone else)
    this.updateLockWarningEditedByName(data.locked_by_name);
    
    // Use all_requests if available, otherwise fall back to pending_requests
    const allRequests = data.all_requests || data.pending_requests || [];
    
    // Find the user's requests (any status) and get the MOST RECENT one
    const userRequests = allRequests.filter(req => 
      req.requester_id.toString() === this.currentUserIdValue
    );
    
    // Sort by created_at (most recent first) and get the latest one
    // If created_at is not available, use id as fallback (assuming higher ID = more recent)
    const userRequest = userRequests.length > 0 
      ? userRequests.sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : a.id;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : b.id;
          return bTime - aTime; // Most recent first
        })[0]
      : null;
    
    if (userRequest) {
      console.log("📋 User has request(s):", userRequests.length, "total, using most recent");
      console.log("  - Most recent request ID:", userRequest.id);
      console.log("  - Most recent request status:", userRequest.status);
      console.log("  - Most recent request created_at:", userRequest.created_at);
      console.log("  - Request requester_id:", userRequest.requester_id);
      console.log("  - Current user ID:", this.currentUserIdValue);
      
      // Handle approved requests - only if user is actually the lock owner
      if (userRequest.status === "approved") {
        const isNowOwner = data.locked_by_id && data.locked_by_id.toString() === this.currentUserIdValue;
        if (isNowOwner) {
          console.log("✅ Most recent request was approved and user is now the lock owner - redirecting");
          this.handleRequestApproved(data);
          return;
        } else {
          // Stale approved request - user is not the current lock owner
          // Ignore this stale request and allow user to create a new one
          console.log("⚠️ Most recent request is approved but user is NOT the current lock owner (stale request)");
          console.log("  - Current lock owner ID:", data.locked_by_id);
          console.log("  - User ID:", this.currentUserIdValue);
          console.log("  - Ignoring stale approved request, allowing new request");
          // Don't return here - fall through to show the request button below
        }
      } else if (userRequest.status === "pending" || userRequest.status === "denied" || userRequest.status === "cancelled") {
        // For pending: only show status if request owner is still the current lock owner (3-user scenario: lock transferred A→B, C had requested from A → show "Request" for B)
        const requestOwnerIsCurrentLockOwner = userRequest.owner_id?.toString() === data.locked_by_id?.toString();
        if (userRequest.status === "pending" && !requestOwnerIsCurrentLockOwner) {
          console.log("⚠️ User's pending request is for previous lock owner (stale); showing Request button for current owner");
          // Fall through to "no request" path below to show Request to Take Over button
        } else {
          // For pending (same owner), denied, cancelled - update the UI
          const requestStatus = document.getElementById("lock-request-status");
          if (requestStatus) {
            console.log("🔄 Updating request status display for request ID:", userRequest.id);
            this.fetchAndUpdateRequestStatus(userRequest.id);
          } else {
            console.warn("⚠️ lock-request-status element not found in DOM");
          }
          return;
        }
      }
      // If we get here, it means we have a stale approved request - fall through to show request button
    }
    
    // No request found for this user yet
    // Check if we're on lock_warning page and post is still locked by someone else
    if (data.locked && data.locked_by_id && data.locked_by_id.toString() !== this.currentUserIdValue) {
      console.log("🔒 Post still locked by another user, checking if we should show request button");
      const requestStatus = document.getElementById("lock-request-status");
      if (requestStatus) {
        // Check if button already exists
        const existingButton = requestStatus.querySelector('button[onclick*="handleRequestTakeover"]');
        if (!existingButton) {
          // Show the request button (user hasn't created a request yet)
          console.log("📝 No request button found, showing it");
          requestStatus.innerHTML = `
            <div style="text-align: center; padding: 20px; background-color: #f8f9fa; border-radius: 4px; margin: 20px 0;">
              <p style="margin-bottom: 15px;">No pending request yet.</p>
              <button type="button" 
                      id="request-takeover-button"
                      onclick="handleRequestTakeover(event, ${this.postIdValue})"
                      class="button"
                      style="padding: 10px 20px; background-color: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
                Request to Take Over Editing
              </button>
            </div>
          `;
        }
      }
      return;
    }
    
    // No pending request found and post is not locked - might have been unlocked
    // Only reload if we're not on the lock_warning page (which would redirect anyway)
    const isOnLockWarningPage = document.getElementById('lock-request-status') !== null;
    if (!isOnLockWarningPage) {
      console.log("⚠️ No pending request found for user and not on lock warning page - reloading page");
      window.location.reload();
    } else {
      console.log("ℹ️ On lock warning page but no request found - post may have been unlocked");
    }
  }

  updateLockedByOtherUI(data) {
    console.log("🔒 Post locked by another user");
    
    // Show lock warning if not already shown
    const lockWarning = document.querySelector('.lock-warning-container');
    if (!lockWarning && data.locked_by_name) {
      // This would typically be handled by server-side rendering
    }
  }

  updateUnlockedUI(data) {
    console.log("🔓 Post unlocked or user can edit");
    
    // Hide lock warnings if any
    const lockWarning = document.querySelector('.lock-warning-container');
    if (lockWarning) {
      lockWarning.style.display = "none";
    }
  }

  handleRequestApproved(data) {
    console.log("✅✅✅ handleRequestApproved called");
    console.log("  - Post ID:", this.postIdValue);
    console.log("  - Current User ID:", this.currentUserIdValue);
    console.log("  - Locked by ID:", data.locked_by_id);
    console.log("  - Data:", data);
    
    // Safety check: Only redirect if user is actually the current lock owner
    const isNowOwner = data.locked_by_id && data.locked_by_id.toString() === this.currentUserIdValue;
    if (!isNowOwner) {
      console.warn("⚠️ handleRequestApproved called but user is NOT the lock owner - aborting redirect");
      console.warn("  - Current lock owner ID:", data.locked_by_id);
      console.warn("  - User ID:", this.currentUserIdValue);
      console.warn("  - This is likely a stale approved request, not redirecting");
      return;
    }
    
    console.log("✅✅✅ User is the lock owner - proceeding with redirect");

    // Clear any existing redirect timeout to prevent conflicts
    if (window.lockRedirectTimeout) {
      clearTimeout(window.lockRedirectTimeout);
      console.log("🔄 Cleared existing redirect timeout");
    }
    
    const requestStatus = document.getElementById("lock-request-status");
    if (requestStatus) {
      requestStatus.innerHTML = `
        <div style="text-align: center; padding: 20px; background-color: #d4edda; border: 1px solid #28a745; border-radius: 4px; margin: 20px 0;">
          <p style="margin-bottom: 10px; color: #155724;">
            <strong>✅ Request Approved!</strong>
          </p>
          <p style="color: #155724; font-size: 14px;">
            Your request has been approved. Redirecting to edit page...
          </p>
        </div>
      `;
      console.log("✅ Updated request status UI");
    } else {
      console.warn("⚠️ lock-request-status element not found");
    }
    
    // Redirect to edit page
    const editUrl = `/admin/posts/${this.postIdValue}/edit`;
    console.log("🔄 Scheduling redirect to:", editUrl);

    // Use a unique timeout ID to prevent conflicts
    window.lockRedirectScheduled = true;
    window.lockRedirectTimeout = setTimeout(() => {
      console.log("🚀 Executing redirect to edit page");
      console.log("  - URL:", editUrl);
      window.location.href = editUrl;
    }, 500);

    console.log("✅ Redirect scheduled for .5 seconds");
  }

  appendRequestToList(request) {
    const container = document.getElementById("lock-takeover-requests");
    if (!container) {
      console.warn("⚠️ lock-takeover-requests container not found");
      return;
    }
    
    // Check if already exists
    const existing = document.getElementById(`lock-takeover-request-${request.id}`);
    if (existing) {
      console.log("⚠️ Request item already exists for request:", request.id);
      return;
    }
    
    // Fetch request HTML
    fetch(`/admin/posts/${this.postIdValue}/takeover_request_item?request_id=${request.id}`, {
      headers: {
        "Accept": "text/html",
        "X-CSRF-Token": this.getCSRFToken()
      }
    })
    .then(response => response.text())
    .then(html => {
      if (html.trim()) {
        // Double-check it doesn't exist (race condition)
        const existingCheck = document.getElementById(`lock-takeover-request-${request.id}`);
        if (existingCheck) {
          console.log("⚠️ Request item was added by another process, skipping");
          return;
        }
        
        container.insertAdjacentHTML("beforeend", html);
        console.log("✅ Request item appended for request:", request.id);
      }
    })
    .catch(error => {
      console.error("Error fetching request item:", error);
    });
  }

  appendModalForRequest(request) {
    // Check if already exists (check both in container and body)
    const existing = document.getElementById(`lock-takeover-modal-${request.id}`);
    if (existing) {
      console.log("⚠️ Modal already exists for request:", request.id);
      return;
    }
    
    // Try to find container, but if not found, append to body directly
    let container = document.getElementById("lock-takeover-modals");
    if (!container) {
      // Create container on body if it doesn't exist (outside form)
      container = document.createElement('div');
      container.id = 'lock-takeover-modals';
      document.body.appendChild(container);
      console.log("📦 Created lock-takeover-modals container on body");
    }
    
    // Fetch modal HTML from server
    fetch(`/admin/posts/${this.postIdValue}/takeover_request_item?request_id=${request.id}&modal=true`, {
      headers: {
        "Accept": "text/html",
        "X-CSRF-Token": this.getCSRFToken()
      }
    })
    .then(response => response.text())
    .then(html => {
      if (html.trim()) {
        // Double-check it doesn't exist (race condition)
        const existingCheck = document.getElementById(`lock-takeover-modal-${request.id}`);
        if (existingCheck) {
          console.log("⚠️ Modal was added by another process, skipping");
          return;
        }
        
        container.insertAdjacentHTML("beforeend", html);
        document.body.classList.add('modal-open');
        console.log("✅ Modal appended for request:", request.id);
      }
    })
    .catch(error => {
      console.error("Error fetching modal:", error);
    });
  }

  fetchAndUpdateRequestStatus(requestId) {
    console.log("📡 Fetching request status for ID:", requestId);
    const requestStatus = document.getElementById("lock-request-status");
    if (!requestStatus) {
      console.error("❌ lock-request-status element not found in DOM");
      return;
    }
    
    console.log("  - Current element HTML length:", requestStatus.innerHTML.length);
    console.log("  - Current element HTML preview:", requestStatus.innerHTML.substring(0, 100));
    
    fetch(`/admin/posts/${this.postIdValue}/takeover_request_item?request_id=${requestId}`, {
      headers: {
        "Accept": "text/html",
        "X-CSRF-Token": this.getCSRFToken()
      }
    })
    .then(response => {
      console.log("  - Response status:", response.status);
      console.log("  - Response OK:", response.ok);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(html => {
      console.log("📥 Received HTML response, length:", html.length);
      console.log("📥 HTML preview:", html.substring(0, 300));
      
      if (!html || !html.trim()) {
        console.warn("⚠️ Empty response from server");
        return;
      }
      
      // The response is the partial content directly (lock_request_status partial)
      // Parse to extract the actual content div
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      
      // The partial returns a div with styling, so find the first div
      let content = html;
      const contentDiv = doc.querySelector('div[style*="text-align"]') || 
                        doc.querySelector('div') ||
                        (doc.body ? doc.body.firstElementChild : null);
      
      if (contentDiv) {
        content = contentDiv.outerHTML;
        console.log("✅ Found content div, using it");
        console.log("  - Content div outerHTML length:", content.length);
      } else if (doc.body && doc.body.innerHTML.trim()) {
        // Fallback: use body's innerHTML (strips html/body tags)
        content = doc.body.innerHTML;
        console.log("✅ Using body innerHTML as fallback");
        console.log("  - Body innerHTML length:", content.length);
      } else {
        // Last resort: use the raw HTML
        console.log("⚠️ No content div found, using raw HTML");
        content = html;
      }
      
      // Check if content is different from current
      const currentContent = requestStatus.innerHTML.trim();
      const newContent = content.trim();
      
      // Always update, even if content appears the same
      // This ensures the DOM is in sync with the server state
      // (Turbo Stream might have updated it, but we want to ensure consistency)
      console.log("🔄 Updating DOM with new content");
      console.log("  - Current length:", currentContent.length);
      console.log("  - New length:", newContent.length);
      console.log("  - Content same?", currentContent === newContent);
      
      // Update the DOM
      requestStatus.innerHTML = content;
      
      // Verify the update
      const updatedContent = requestStatus.innerHTML.trim();
      if (updatedContent === newContent || updatedContent.length > 0) {
        console.log("✅ Request status updated successfully");
        console.log("  - Updated content length:", updatedContent.length);
        console.log("  - Element ID:", requestStatus.id);
        console.log("  - Update verified:", updatedContent.substring(0, 100));
      } else {
        console.error("❌ DOM update failed - content mismatch or empty");
        console.error("  - Expected length:", newContent.length);
        console.error("  - Actual length:", updatedContent.length);
        console.error("  - Actual content:", updatedContent.substring(0, 100));
      }
    })
    .catch(error => {
      console.error("❌ Error fetching request status:", error);
      console.error("  - Error details:", error.message);
      console.error("  - Stack:", error.stack);
    });
  }

  updateLockWarningEditedByName(lockedByName) {
    const nameEl = document.getElementById("lock-warning-edited-by-name");
    if (nameEl && lockedByName) {
      nameEl.textContent = lockedByName;
    }
  }

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : "";
  }
}
